import { ComponentType, createElement, FunctionComponent } from "react";

import { bridge } from "@multiweb/reactCore/bridge";
import { IAppState } from "@multiweb/types/react";
import { NAME } from "./constants";
import { logger } from "./logger";
import { ComponentMap } from "./types";

const { getDIContainer } = bridge;

/**
 * Vrací závislost podle jejího jména. V případě neexistující závislosti vrací undefined.
 * @param dependencyName Jméno požadované závislosti
 */
export const getDependencyIfExists = <T>(dependencyName: string) => {
    const DIContainer = getDIContainer();
    if (!DIContainer.has(dependencyName)) {
        return undefined;
    }
    return DIContainer.create(dependencyName) as T;
};

/**
 * Vytvoří funkci pro získání komponenty zapojené v DIContainer
 * @param defaultComponentMap Výchozí mapování jmen komponent na komponenty. Použije se v případě, kdy komponenta s požadovaným jménem není zaregistrovaná v DIContaineru.
 * Používá se primárně pro účely storybooku, kde je DIContainer mockovaný.
 * @template T Klíče jsou názvy komponenty, hodnoty jejich props
 */
export const createGetInjectedComponent = <T>(defaultComponentMap?: ComponentMap<T>) => (
    /**
     * Vrací komponentu zapojenou v DIContainer
     * @param componentName Název získávané komponenty
     * @throws Vyhodí chybu v případě, že komponenta s požadovaným jménem nebyla nalezena.
     */
    <K extends keyof T>(componentName: K): ComponentType<T[K]> => {
        const injectedComponent = getDIContainer().create(componentName.toString()) as ComponentType<T[K]>;
        const nonNullInjectedComponent = injectedComponent || !defaultComponentMap ? injectedComponent : defaultComponentMap[componentName];

        if (!nonNullInjectedComponent) {
            throw new Error(`[Customization] Component with name ${String(componentName)} is not registered in DIContainer, neither in defaultComponentMap parameter.`);
        } else if (!injectedComponent) {
            logger.info("Component with name %s is not registered in DIContainer. Component from defaultComponentMap parameter is used as fallback.", componentName);
        }

        return nonNullInjectedComponent;
    }
);

/**
 * Vytvoří funkci pro získání komponenty zapojené v DIContainer – komponentu získává až v okamžiku potřeby jejího vykreslení.
 * @param defaultComponentMap Výchozí mapování jmen komponent na komponenty. Použije se v případě, kdy komponenta s požadovaným jménem není zaregistrovaná v DIContaineru.
 * Používá se primárně pro účely storybooku, kde je DIContainer mockovaný.
 * @template T Klíče jsou názvy komponenty, hodnoty jejich props
 */
export const createGetInjectedComponentOnRender = <T>(defaultComponentMap?: ComponentMap<T>) => {
    const getInjectedComponent = createGetInjectedComponent<T>(defaultComponentMap);

    /**
     * Vrací komponentu zapojenou v DIContainer – komponentu získává až v okamžiku potřeby jejího vykreslení.
     * Funkce je využívaná při používání _High Order Component_, například `connect` balíčku `react-redux`.
     * @param componentName Název získávané komponenty
     */
    const getInjectedComponentOnRender = <K extends keyof T>(componentName: K) => {
        // TODO 27863 Název proměnné definuje název zobrazovaný ve snapshot testech
        const InjectedComponent: FunctionComponent<T[K]> = (props) => {
            const injectedComponent = getInjectedComponent(componentName);
            return createElement(injectedComponent, props);
        };
        return InjectedComponent;
    };

    return getInjectedComponentOnRender;
};

/**
 * Vrací podstrom pro custom modul.
 * @param state Stav celé aplikace
 * @param subtreeRootName Název kořene stavu custom modulu
 */
export const getCustomStateSubtree = <T>(state: IAppState, subtreeRootName: string): T => (
    // @ts-ignore Problém je v *unknown* typu *state[NAME]*, předem není známá struktura custom podstromu
    state[NAME][subtreeRootName]
);

/**
 * Vrací funkci pro registraci přepisovatelných komponent do DIContainer.
 * @param replaceableComponents Údaje pro registraci přepisovatelných komponent
 */
export const createRegisterReplaceableComponents = (replaceableComponents: {[componentName: string]: ComponentType<any>}) => (
    /**
     * Registruje přepisovatelné komponenty do DIContainer.
     */
    () => {
        const di = getDIContainer();
        Object.entries(replaceableComponents).forEach(([componentName, component]) => {
            di.registerFactory(componentName, () => component);
        });
    }
);
